import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { authApi } from 'api/auth';
import { setAccessToken } from 'api/client';

function SignupField({
  label,
  field,
  type = 'text',
  placeholder,
  icon,
  showToggle,
  showVal,
  onToggle,
  value,
  error,
  onValueChange,
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
        {label}
      </label>
      <div className="relative">
        <Icon
          name={icon}
          size={16}
          color={error ? 'var(--color-error)' : 'var(--color-text-secondary)'}
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        />
        <input
          type={showToggle ? (showVal ? 'text' : 'password') : type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onValueChange(field, e?.target?.value)}
          className="w-full pl-10 pr-10 py-3 rounded-xl border font-caption text-sm transition-all duration-200"
          style={{
            borderColor: error ? 'var(--color-error)' : 'var(--color-border)',
            background: 'var(--color-background)',
            color: 'var(--color-foreground)',
            outline: 'none',
          }}
          onFocus={(e) => { if (!error) e.target.style.borderColor = 'var(--color-primary)'; }}
          onBlur={(e) => { if (!error) e.target.style.borderColor = 'var(--color-border)'; }}
        />
        {showToggle && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--color-text-secondary)' }}
            onClick={onToggle}
          >
            <Icon name={showVal ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
          </button>
        )}
      </div>
      {error && (
        <div className="flex items-center gap-1.5">
          <Icon name="AlertCircle" size={12} color="var(--color-error)" />
          <span className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>{error}</span>
        </div>
      )}
    </div>
  );
}

export default function SignupForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData?.fullName?.trim()) errs.fullName = 'Full name is required';
    if (!formData?.email) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) errs.email = 'Enter a valid email';
    if (!formData?.company?.trim()) errs.company = 'Company name is required';
    if (!formData?.password) errs.password = 'Password is required';
    else if (formData?.password?.length < 8) errs.password = 'Password must be at least 8 characters';
    if (!formData?.confirmPassword) errs.confirmPassword = 'Please confirm your password';
    else if (formData?.password !== formData?.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (!formData?.agreeTerms) errs.agreeTerms = 'You must agree to the terms';
    return errs;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitError('');
    setLoading(true);

    try {
      const response = await authApi.signup({
        companyName: formData?.company,
        name: formData?.fullName,
        email: formData?.email,
        password: formData?.password,
      });

      if (response?.token) {
        setAccessToken(response.token);
      }

      setLoading(false);
      // Sprint 9 (Part 89 §89.5): new orgs land in the 10-minute onboarding
      // wizard (workspace → WhatsApp → import → Today).
      navigate('/onboarding');
    } catch (error) {
      setLoading(false);
      setSubmitError(error.message || 'Signup failed. Please try again.');
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const getPasswordStrength = (pwd) => {
    if (!pwd) return null;
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    if (score <= 1) return { label: 'Weak', color: '#DC2626', width: '25%' };
    if (score === 2) return { label: 'Fair', color: '#D97706', width: '50%' };
    if (score === 3) return { label: 'Good', color: '#059669', width: '75%' };
    return { label: 'Strong', color: '#059669', width: '100%' };
  };

  const strength = getPasswordStrength(formData?.password);

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <SignupField
        label="Full Name"
        field="fullName"
        placeholder="Priya Sharma"
        icon="User"
        showToggle={false}
        showVal={false}
        onToggle={() => {}}
        value={formData?.fullName}
        error={errors?.fullName}
        onValueChange={handleChange}
      />
      <SignupField
        label="Work Email"
        field="email"
        type="email"
        placeholder="priya@company.com"
        icon="Mail"
        showToggle={false}
        showVal={false}
        onToggle={() => {}}
        value={formData?.email}
        error={errors?.email}
        onValueChange={handleChange}
      />
      <SignupField
        label="Company Name"
        field="company"
        placeholder="Tata Consultancy Services"
        icon="Building2"
        showToggle={false}
        showVal={false}
        onToggle={() => {}}
        value={formData?.company}
        error={errors?.company}
        onValueChange={handleChange}
      />
      <SignupField
        label="Password"
        field="password"
        icon="Lock"
        showToggle
        showVal={showPassword}
        onToggle={() => setShowPassword((p) => !p)}
        placeholder="Min. 8 characters"
        value={formData?.password}
        error={errors?.password}
        onValueChange={handleChange}
      />
      {/* Password strength */}
      {formData?.password && strength && (
        <div className="flex flex-col gap-1 -mt-2">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-border)' }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ width: strength?.width, background: strength?.color }} />
          </div>
          <span className="font-caption text-xs" style={{ color: strength?.color }}>Password strength: {strength?.label}</span>
        </div>
      )}
      <SignupField
        label="Confirm Password"
        field="confirmPassword"
        icon="ShieldCheck"
        showToggle
        showVal={showConfirm}
        onToggle={() => setShowConfirm((p) => !p)}
        placeholder="Re-enter password"
        value={formData?.confirmPassword}
        error={errors?.confirmPassword}
        onValueChange={handleChange}
      />
      {/* Terms */}
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="agreeTerms"
            checked={formData?.agreeTerms}
            onChange={(e) => handleChange('agreeTerms', e?.target?.checked)}
            className="mt-0.5 rounded flex-shrink-0"
            style={{ accentColor: 'var(--color-primary)', width: 16, height: 16 }}
          />
          <label htmlFor="agreeTerms" className="font-caption text-sm cursor-pointer" style={{ color: 'var(--color-text-secondary)' }}>
            I agree to the Terms of Service and Privacy Policy
          </label>
        </div>
        {errors?.agreeTerms && (
          <div className="flex items-center gap-1.5 ml-6">
            <Icon name="AlertCircle" size={12} color="var(--color-error)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>{errors?.agreeTerms}</span>
          </div>
        )}
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-caption font-semibold text-sm transition-all duration-200 mt-1"
        style={{
          background: loading ? 'var(--color-muted)' : 'var(--color-primary)',
          color: loading ? 'var(--color-text-secondary)' : '#FFFFFF',
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? (
          <>
            <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: 'var(--color-text-secondary)', borderTopColor: 'transparent' }} />
            Creating account...
          </>
        ) : (
          <>
            <Icon name="UserPlus" size={16} color="#FFFFFF" />
            Create Free Account
          </>
        )}
      </button>
      {submitError && (
        <div className="flex items-center gap-1.5">
          <Icon name="AlertTriangle" size={12} color="var(--color-error)" />
          <span className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>{submitError}</span>
        </div>
      )}
    </form>
  );
}
